/**
 * Wire contract for the account Product Usage tab.
 *
 * Canonical shape, shared by the Express handler and the React tab. Everything here is
 * derived from the warehouse mirror. Nothing on this tab is computed at request time beyond
 * formatting-level arithmetic: the numbers a rep would act on (`position`, `runway`) are
 * carried through from Snowflake rather than recalculated, and the position is computed
 * ONCE server-side so that no consumer subtracts, divides, or compares two Measures itself.
 */
import { z } from 'zod';

/**
 * A number that says what it is.
 *
 * Every figure the tab can compare against another travels with its unit, the basis it was
 * measured on, and the window it covers, so "16.0M" next to "8.9M" is never two numbers
 * from different ledgers presented as if they were one. `basis` distinguishes the contract
 * model (what the pacing model counts against the contract), the ledger (the account's
 * actual credit balance), and Terra's own observed mirror (the account-day rows).
 */
export const MeasureUnitSchema = z.enum(['credits', 'actions', 'ratio', 'percent']);
export type MeasureUnit = z.infer<typeof MeasureUnitSchema>;

export const MeasureBasisSchema = z.enum(['ledger', 'contract', 'observed']);
export type MeasureBasis = z.infer<typeof MeasureBasisSchema>;

export const MeasureWindowSchema = z.enum(['l7', 'l30', 'contract_to_date', 'as_of_day']);
export type MeasureWindow = z.infer<typeof MeasureWindowSchema>;

export const MeasureSchema = z.object({
  value: z.number().nullable(),
  unit: MeasureUnitSchema,
  basis: MeasureBasisSchema,
  window: MeasureWindowSchema,
  /** The day the value is true for, `YYYY-MM-DD`. Null when the source carries no date. */
  asOf: z.string().nullable(),
});
export type Measure = z.infer<typeof MeasureSchema>;

/**
 * One day of the credit pacing chart.
 *
 * `consumed` is CUMULATIVE contract-to-date, straight from the warehouse, not a daily delta
 * that the client re-accumulates. Re-accumulating would drift from the warehouse's own total
 * as soon as a refund or an exclusion lands mid-window.
 */
export const PacingPointSchema = z.object({
  day: z.string().nullable(),
  consumed: z.number().nullable(),
  adjustedConsumed: z.number().nullable(),
  expected: z.number().nullable(),
  balance: z.number().nullable(),
  contractCredits: z.number().nullable(),
});
export type PacingPoint = z.infer<typeof PacingPointSchema>;

/**
 * The credit position, as the warehouse states it.
 *
 * `paceRatio` and `paceStatus` are the ADJUSTED variants, corrected for admin-added and
 * excluded credits. `rawPaceRatio`/`rawPaceStatus` travel alongside so the tab can say the
 * two disagree instead of quietly choosing; on an account carrying material admin adds the
 * uncorrected pair answers a different question. Neither is ever recomputed client-side.
 *
 * The only arithmetic in this object is the two booleans, `amended` and `balanceDiffers`,
 * which exist precisely so that comparison happens once, here, and not in every consumer.
 */
export const CreditPositionSchema = z.object({
  /** Credits the contract currently entitles the account to, after amendments. */
  inContract: MeasureSchema,
  /** What was bought at signing. Differs from `inContract` when the contract was amended. */
  originalPurchase: MeasureSchema,
  /** True only when both `inContract` and `originalPurchase` are known and differ. */
  amended: z.boolean(),
  used: MeasureSchema,
  /** Share of the contract consumed, as the warehouse reports it. */
  pctUsed: MeasureSchema,
  /** The LEDGER balance, a different basis from `inContract` minus `used`. */
  balance: MeasureSchema,
  /**
   * True when the ledger balance and the contract arithmetic disagree by more than 5% of the
   * contract. Admin adds, exclusions, and tranche resets all produce this; the tab explains
   * rather than picking one.
   */
  balanceDiffers: z.boolean(),
  expectedByNow: MeasureSchema,
  paceRatio: MeasureSchema,
  paceStatus: z.string().nullable(),
  rawPaceRatio: MeasureSchema,
  rawPaceStatus: z.string().nullable(),
  /**
   * Trailing 30-day burn over the contract's daily allowance, a DIFFERENT question from
   * `paceRatio`, which is contract-to-date. An account can sit under pace on the year while
   * burning 2x plan this month; both are shown because the divergence is the signal.
   */
  paceRatioL30d: MeasureSchema,
  /** The day the trailing window is anchored on, so the rate's basis is inspectable. */
  l30AnchorDay: z.string().nullable(),
  /** Fraction of the contract elapsed, 0 to 1. */
  pctElapsed: z.number().nullable(),
  daysElapsed: z.number().nullable(),
  daysLeft: z.number().nullable(),
  /**
   * Set when admin adds or exclusions move the arithmetic. The tab shows a note: "used vs
   * purchased" is misleading on an account whose balance was topped up mid-term.
   */
  hasMaterialAdminAdds: z.boolean().nullable(),
  cumulativeAdminAdded: z.number().nullable(),
  /** Cumulative series for the chart, oldest first. Empty when no pacing row exists. */
  series: z.array(PacingPointSchema),
  /** The warehouse's as-of date. Shown wherever the numbers are, because dbt may not have rebuilt today. */
  asOf: z.string().nullable(),
});
export type CreditPosition = z.infer<typeof CreditPositionSchema>;

/** One day of the action pacing chart. `used` is cumulative contract-to-date, like `PacingPoint.consumed`. */
export const ActionPointSchema = z.object({
  day: z.string().nullable(),
  used: z.number().nullable(),
  expected: z.number().nullable(),
});
export type ActionPoint = z.infer<typeof ActionPointSchema>;

/**
 * The action position, present only on new pricing, where actions are the metered unit.
 *
 * Every value is read from the warehouse; a mirror row synced before the pacing query
 * carried action columns yields nulls throughout rather than a missing object.
 */
export const ActionPositionSchema = z.object({
  allowance: MeasureSchema,
  used: MeasureSchema,
  expectedByNow: MeasureSchema,
  paceRatio: MeasureSchema,
  paceStatus: z.string().nullable(),
  /** Raw fraction 0 to 1 as the warehouse stores it, not a percentage. */
  pctOfAllowance: MeasureSchema,
  series: z.array(ActionPointSchema),
  asOf: z.string().nullable(),
});
export type ActionPosition = z.infer<typeof ActionPositionSchema>;

/**
 * The workspace's position against its contract, discriminated on pricing model.
 *
 * Legacy pricing meters credits only; new pricing meters both. The arms are strict so a
 * legacy position cannot leak an `actions` key and a new-pricing one cannot drop it: the
 * API handler safeParses the response, and the tab branches on `pricingModel` alone.
 */
export const ProductUsagePositionSchema = z.discriminatedUnion('pricingModel', [
  z.object({ pricingModel: z.literal('legacy'), credits: CreditPositionSchema }).strict(),
  z
    .object({ pricingModel: z.literal('new'), credits: CreditPositionSchema, actions: ActionPositionSchema })
    .strict(),
]);
export type ProductUsagePosition = z.infer<typeof ProductUsagePositionSchema>;

/**
 * Runway routing. A surface MUST read `basis` before rendering anything about credits
 * running out: on `budget_period` the balance is what is left of the CURRENT TRANCHE, so
 * dividing it by daily burn gives days to budget reset, not days of entitlement.
 */
export const RunwaySchema = z.object({
  basis: z.enum(['contract', 'budget_period']).nullable(),
  isTranchedBudget: z.boolean().nullable(),
  /** True = do not surface this account as an expansion/early-renewal candidate on runway grounds. */
  suppressExpansionFlag: z.boolean().nullable(),
  daysToBudgetReset: z.number().nullable(),
  nextBudgetPeriodStart: z.string().nullable(),
  budgetPeriodMonths: z.number().nullable(),
  likelyMidContractRenewal: z.boolean().nullable(),
});
export type Runway = z.infer<typeof RunwaySchema>;

export const ContractSchema = z.object({
  pricingModel: z.enum(['new', 'legacy']).nullable(),
  /** New pricing should carry both credits and actions; set when it does not. */
  pricingAnomaly: z.boolean().nullable(),
  planType: z.string().nullable(),
  productTier: z.string().nullable(),
  arr: z.number().nullable(),
  totalContractValue: z.number().nullable(),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  term: z.number().nullable(),
  invoiceInterval: z.string().nullable(),
  contractNumber: z.number().nullable(),
  numPaidCredits: z.number().nullable(),
  numPaidActions: z.number().nullable(),
  /**
   * Contracted unit prices, dollars per unit, straight from `CONTRACTS`.
   *
   * `costPerCredit` is `COST_OF_CREDITS / NUM_PAID_CREDITS`, the rate on the order form.
   * `blendedCostPerCredit` puts contractually granted free credits (forgiven + promotion) in
   * the denominator too, so it is the effective rate and is never above `costPerCredit`; the
   * two differ on roughly one active enterprise contract in nine. Actions carry no free-unit
   * column, so the warehouse's blended action cost equals `costPerAction` on every row and is
   * not carried. Typical magnitudes are $0.01 to $0.06 per credit and $0.003 to $0.008 per
   * action, so any formatter needs sub-cent precision.
   */
  costPerCredit: z.number().nullable(),
  blendedCostPerCredit: z.number().nullable(),
  costPerAction: z.number().nullable(),
  /** CPJ is a THIRD metering construct: an account can pace fine on credits and still be capped here. */
  hasCpj: z.boolean().nullable(),
  cpjType: z.string().nullable(),
  cpjShareOfLimitSpent: z.number().nullable(),
  cpjContractualLimit: z.number().nullable(),
  cpjTotalSearchActions: z.number().nullable(),
});
export type Contract = z.infer<typeof ContractSchema>;

/** Credits attributed to a product surface over the window, for the mix breakdown. */
export const ProductMixEntrySchema = z.object({
  key: z.string(),
  label: z.string(),
  credits: z.number(),
});
export type ProductMixEntry = z.infer<typeof ProductMixEntrySchema>;

/**
 * Observed consumption from the account-day mirror, independent of the contract.
 *
 * Every figure is a Measure on the `observed` basis with `asOf` = `throughDay`, so it can
 * sit beside a contract-basis figure without the two being mistaken for the same ledger.
 */
export const ConsumptionSchema = z.object({
  creditsL7: MeasureSchema,
  creditsL30: MeasureSchema,
  /** L30 over days with data, not over 30: a mid-contract start would otherwise read as a slump. */
  creditsPerDay: MeasureSchema,
  /**
   * METERED actions, the unit the contract bills on: the S3 billing stream's `actions_spent`
   * (`WORKSPACE_CONSUMPTION_DAILY.ACTION_EXECUTION_CREDITS`, a count despite the name), the
   * same currency as the pacing model's `cumu_actions_spent` and the contract's
   * `num_paid_actions`, populated on every pricing model. NOT `TOTAL_ACTIONS`, which counts
   * every action-worker execution including free and zero-cost ones and runs several times
   * higher (Anthropic: 23.1M runs vs 6.2M metered over the same 30 days), and NOT
   * `ACTIONS_RECOGNIZED`, the rev-rec drawdown that is zero on credit-only contracts. Null,
   * not zero, while the mirror has no metered figure for the window.
   */
  actionsL7: MeasureSchema,
  actionsL30: MeasureSchema,
  /** Same divide-by-days-with-data rule as `creditsPerDay`. */
  actionsPerDay: MeasureSchema,
  daysObserved: z.number(),
  /** Newest day present in the mirror, so a stale sync is visible rather than silent. */
  throughDay: z.string().nullable(),
  /** L30 credits split by product, largest first. */
  productMix: z.array(ProductMixEntrySchema),
});
export type Consumption = z.infer<typeof ConsumptionSchema>;

/** Adoption surface by surface — what they actually turned on. */
export const AdoptionSchema = z.object({
  audiencesStage: z.string().nullable(),
  audiencesActivelyUsing: z.boolean().nullable(),
  audienceRecordsTotal: z.number().nullable(),
  imports: z.number().nullable(),
  segments: z.number().nullable(),
  hasSignals: z.boolean().nullable(),
  hasSignalsActive: z.boolean().nullable(),
  signalTypes: z.number().nullable(),
  signalActionsL30: z.number().nullable(),
  hasAdSync: z.boolean().nullable(),
  adSyncs: z.number().nullable(),
  adProviders: z.string().nullable(),
  adMatchRate: z.number().nullable(),
  workflows: z.number().nullable(),
  liveWorkflows: z.number().nullable(),
  workflowRunsL30: z.number().nullable(),
  agentWorkflows: z.number().nullable(),
  mcpActivated: z.boolean().nullable(),
  mcpRunsL30: z.number().nullable(),
  mcpUsers: z.number().nullable(),
  mcpPlatforms: z.string().nullable(),
  crmWriteback: z.boolean().nullable(),
  /** ' · '-joined lists straight from the warehouse; the tab splits them for display. */
  integrationsL30: z.string().nullable(),
  integrationCountL30: z.number().nullable(),
  enrichmentKindsL30: z.string().nullable(),
  enrichmentKindCountL30: z.number().nullable(),
  peakActiveTablesL30: z.number().nullable(),
  peakActiveFieldsL30: z.number().nullable(),
  actionsL30: z.number().nullable(),
  actionsFailedL30: z.number().nullable(),
  /** Share of credits originating in workflows rather than tables, 0–100. */
  pctCreditsFromWorkflows: z.number().nullable(),
  lastActivityDay: z.string().nullable(),
});
export type Adoption = z.infer<typeof AdoptionSchema>;

/**
 * One person in the workspace.
 *
 * `name` and `salesforceContactId` are filled from Terra's own Salesforce contacts by
 * email — the warehouse `USERS` table carries email, title and persona but no name.
 */
/**
 * A contact can hold SEVERAL roles at once — "Champion · Key builder" is the common and
 * important case, so this is a set, not one label. Ordered most-significant first by the
 * read path so a UI can render them in order without re-sorting.
 */
export const ContactRoleSchema = z.enum(['champion', 'key_builder', 'builder']);
export type ContactRole = z.infer<typeof ContactRoleSchema>;

export const UsagePersonSchema = z.object({
  clayUserId: z.string(),
  email: z.string().nullable(),
  name: z.string().nullable(),
  salesforceContactId: z.string().nullable(),
  title: z.string().nullable(),
  builderTier: z.string().nullable(),
  recurringAiBuilder: z.boolean().nullable(),
  credits: z.number().nullable(),
  aiCredits: z.number().nullable(),
  actions: z.number().nullable(),
  tablesCreated: z.number().nullable(),
  activeBuilderDays: z.number().nullable(),
  lastActiveDay: z.string().nullable(),

  /** Every role that applies. Empty for a user who has neither built nor championed. */
  roles: z.array(ContactRoleSchema),
  /** Credits attributed to this person on the CURRENT contract. */
  attributableCredits: z.number().nullable(),
  attributableActions: z.number().nullable(),
  /**
   * Share of the contract's ATTRIBUTED consumption — attributed credits over the total
   * attributed across the workspace. Sums to 1 across people and cannot exceed it. Not a
   * share of the metered total; see `attributionCoverage` for how the two relate.
   */
  creditShareOfAttributed: z.number().nullable(),
  activeDaysL30: z.number().nullable(),
  daysSinceLastOpen: z.number().nullable(),
  isActiveL30: z.boolean().nullable(),
});
export type UsagePerson = z.infer<typeof UsagePersonSchema>;

/**
 * One Clay table's consumption. `creditsLifetime` is since the table's first use, which may
 * predate the current contract — the warehouse sums across the workspace's contracts, so a
 * renewal does not restart the count.
 */
export const UsageTableSchema = z.object({
  tableId: z.string(),
  tableName: z.string().nullable(),
  creditsLifetime: z.number().nullable(),
  creditsL30: z.number().nullable(),
  actionsLifetime: z.number().nullable(),
  actionsL30: z.number().nullable(),
  distinctRecords: z.number().nullable(),
  firstConsumptionDay: z.string().nullable(),
  lastConsumptionDay: z.string().nullable(),
});
export type UsageTable = z.infer<typeof UsageTableSchema>;

/** One workspace's full picture. An account with several gets several of these. */
export const WorkspaceUsageSchema = z.object({
  workspaceId: z.string(),
  accountName: z.string().nullable(),
  consumption: ConsumptionSchema,
  /** Null when the workspace has no contract row, or its pricing model is unknown. */
  position: ProductUsagePositionSchema.nullable(),
  runway: RunwaySchema.nullable(),
  contract: ContractSchema.nullable(),
  adoption: AdoptionSchema.nullable(),
  people: z.array(UsagePersonSchema),
  peopleWindowDays: z.number().nullable(),
  /** Highest-spending tables first. Empty when the workspace has no table consumption. */
  tables: z.array(UsageTableSchema),
  /**
   * Attributed credits over the pacing model's metered consumption, for the current contract.
   *
   * 1.0 means every metered credit is attributable to a person. Above 1 means shared tables
   * attribute the same spend to several builders; below 1 means some consumption has no
   * attributable user (service accounts, scheduled runs). Observed range across live accounts
   * is 0.93 to 1.11. Surfaced so the People shares are not mistaken for shares of the invoice.
   */
  attributionCoverage: z.number().nullable(),
  /** When the mirror last refreshed this workspace. */
  syncedAt: z.string().nullable(),
});
export type WorkspaceUsage = z.infer<typeof WorkspaceUsageSchema>;

/**
 * Account-level header facts that are not per-workspace.
 *
 * `health*` and the Slack channels come from Salesforce; TCV comes from the contract mirror.
 * All four Slack fields are nullable because most accounts have no channel, and a null
 * must render as nothing rather than as a placeholder. Written by the sfdc-object-sync
 * bulk leg since PR #1758.
 */
export const AccountHeaderFactsSchema = z.object({
  /** Green / Yellow / Red. Null means genuinely ungraded, which is not the same as healthy. */
  healthRating: z.string().nullable(),
  healthRatingDescr: z.string().nullable(),
  healthLastUpdated: z.string().nullable(),
  /** Total contract value, from the contract mirror rather than Salesforce. */
  totalContractValue: z.number().nullable(),
  arr: z.number().nullable(),
  customerSlackName: z.string().nullable(),
  customerSlackChannelId: z.string().nullable(),
  internalSlackChannel: z.string().nullable(),
  internalSlackId: z.string().nullable(),
});
export type AccountHeaderFacts = z.infer<typeof AccountHeaderFactsSchema>;

export const ProductUsageResponseSchema = z.object({
  salesforceAccountId: z.string(),
  accountName: z.string().nullable(),
  header: AccountHeaderFactsSchema,
  /**
   * Empty when the account has no mirrored usage — a plan type outside the mirrored book,
   * or one the nightly sync has not reached. The tab says which; an empty array rendered as
   * zeros would read as "this customer does nothing".
   */
  workspaces: z.array(WorkspaceUsageSchema),
  /** True when the account is absent from the warehouse's mirrored book entirely. */
  notMirrored: z.boolean(),
  generatedAt: z.string(),
});
export type ProductUsageResponse = z.infer<typeof ProductUsageResponseSchema>;
